import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  Placement,
  FloatingArrow,
} from "@floating-ui/react";
import "./Tooltip.scss";

export type TooltipType = "ellipsis" | "info";

export interface TooltipOptions {
  placement?: Placement;
  delayShow?: number;
  showOnOverflow?: boolean;
}

const OVERFLOW_TOLERANCE_PX = 1;

const isElementOverflowing = (element: HTMLElement): boolean =>
  element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE_PX ||
  element.scrollHeight - element.clientHeight > OVERFLOW_TOLERANCE_PX;

const useTooltip = ({
  placement = "top",
  delayShow = 0,
  showOnOverflow = false,
}: TooltipOptions = {}) => {
  const [open, setOpen] = useState<boolean>(false);
  const arrowRef = useRef(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      const trigger = triggerRef.current;
      const isBlockedByOverflowGate =
        showOnOverflow && !!trigger && !isElementOverflowing(trigger);

      if (nextOpen && isBlockedByOverflowGate) {
        return;
      }

      setOpen(nextOpen);
    },
    [showOnOverflow]
  );

  const data = useFloating({
    placement,
    open,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: true,
    delay: {
      open: delayShow,
      close: 0,
    },
  });
  const focus = useFocus(context, {
    enabled: true,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      triggerRef,
      ...interactions,
      ...data,
    }),
    [open, setOpen, arrowRef, triggerRef, interactions, data]
  );
};

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

const Tooltip = ({
  children,
  content,
  type = "ellipsis",
  className = "",
  ...options
}: {
  children: ReactNode;
  content: string | ReactNode;
  type?: TooltipType;
  className?: string;
} & TooltipOptions) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return !!content ? (
    <TooltipContext.Provider value={tooltip}>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent
        className={`tooltip-content ${type === "info" ? "is-info" : ""} ${className}`}
        type={type}
      >
        {content}
      </TooltipContent>
    </TooltipContext.Provider>
  ) : (
    <>{children}</>
  );
};

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement>
>(function TooltipTrigger({ children, ...props }, propRef) {
  const context = useTooltipContext();
  const childrenRef = (children as any)?.ref;
  const ref = useMergeRefs([
    context.refs.setReference,
    context.triggerRef,
    propRef,
    childrenRef,
  ]);

  if (React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        "data-state": context.open ? "open" : "closed",
      })
    );
  }

  return (
    <div
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      {...context.getReferenceProps(props)}
      className={"tooltip-trigger-wrap"}
    >
      {children}
    </div>
  );
});

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipContent({ style, type, ...props }, propRef) {
  const { context: floatingContext, ...context } = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style,
        }}
        {...context.getFloatingProps(props)}
      >
        {props.children}
        {type === "info" && (
          <FloatingArrow
            context={floatingContext}
            className={"tooltip-arrow"}
            ref={context.arrowRef}
            height={4}
            width={8}
            tipRadius={1}
          />
        )}
      </div>
    </FloatingPortal>
  );
});

export default Tooltip;
